import {Observable, of, pipe} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage,
    ArrayEncodedMdUpdateMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {MdSubscriptionSubject, SecurityMarketData} from './md-subscription-subject';

enum PrivateMdWebSocketMessageType {
    PRIVATE_MD_SNAPSHOT = 'PRIVATE_MD_SNAPSHOT',
    PRIVATE_MD_UPDATE   = 'PRIVATE_MD_UPDATE'
}

interface PrivateMdWebSocketMessage extends WebSocketMessage {
    messageType: PrivateMdWebSocketMessageType.PRIVATE_MD_SNAPSHOT;
    data: ArrayEncodedMdMessage[];
}

export class PrivateMdSubscriptionSubject extends MdSubscriptionSubject {

    public messageType: string;

    private aggregatedPrivateMessageTypeToCommonDict: {[aggregatedMessageType: string]: PrivateMdWebSocketMessageType};

    constructor(
        protected override aggregationScale: number,
        protected override securityService: SecurityService
    ) {
        super(aggregationScale, securityService);
    }

    protected override createSubjectType(): void {
        this.type = {
            name: `PRIVATE_MD${this.aggregationSuffix}`,
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: `SUBSCRIBE_PRIVATE_MD${this.aggregationSuffix}`,
                UNSUBSCRIBE: `UNSUBSCRIBE_PRIVATE_MD${this.aggregationSuffix}`
            },
            messagePipe: pipe(
                filter((webSocketMessage: WebSocketMessage) =>
                    !!this.aggregatedPrivateMessageTypeToCommonDict[webSocketMessage.messageType]),

                mergeMap((mdWebSocketMessage: PrivateMdWebSocketMessage): Observable<SecurityMarketData> => {
                    const mdMessageContainers = this.readPrivateMdWebSocketMessage(mdWebSocketMessage);

                    return of(...mdMessageContainers);
                })
            ),
            subjectPipeFactory: subject => this.createSubjectPipe(subject),
            deprecateSubject: subject => this.deprecateSubject(subject)
        };
    }

    protected override createWebSocketMessageTypes(): void {
        const snapshotMessage = [PrivateMdWebSocketMessageType.PRIVATE_MD_SNAPSHOT, this.aggregationSuffix].join('');
        const updateMessage = [PrivateMdWebSocketMessageType.PRIVATE_MD_UPDATE, this.aggregationSuffix].join('');

        this.aggregatedPrivateMessageTypeToCommonDict = {
            [snapshotMessage]: PrivateMdWebSocketMessageType.PRIVATE_MD_SNAPSHOT,
            [updateMessage]:   PrivateMdWebSocketMessageType.PRIVATE_MD_UPDATE
        };

        // Support new format with forced #1
        if (this.aggregationScale === 1) {
            const newSnapshotMessage = [PrivateMdWebSocketMessageType.PRIVATE_MD_SNAPSHOT, '#1'].join('');
            const newUpdateMessage = [PrivateMdWebSocketMessageType.PRIVATE_MD_UPDATE, '#1'].join('');

            this.aggregatedPrivateMessageTypeToCommonDict[newSnapshotMessage] =
                PrivateMdWebSocketMessageType.PRIVATE_MD_SNAPSHOT;
            this.aggregatedPrivateMessageTypeToCommonDict[newUpdateMessage] =
                PrivateMdWebSocketMessageType.PRIVATE_MD_UPDATE;
        }
    }

    protected readPrivateMdWebSocketMessage(mdWebSocketMessage: PrivateMdWebSocketMessage): SecurityMarketData[] {
        let securitiesMd: SecurityMarketData[];
        const commonMessageType: PrivateMdWebSocketMessageType =
                  this.aggregatedPrivateMessageTypeToCommonDict[mdWebSocketMessage.messageType];

        switch (commonMessageType) {
            case PrivateMdWebSocketMessageType.PRIVATE_MD_SNAPSHOT:
                securitiesMd = this.readMdSnapshot(mdWebSocketMessage.data as ArrayEncodedMdSnapshotMessage[]);
                break;
            case PrivateMdWebSocketMessageType.PRIVATE_MD_UPDATE:
                securitiesMd = this.readMdUpdate(mdWebSocketMessage.data as ArrayEncodedMdUpdateMessage[]);
                break;
        }

        return securitiesMd;
    }

}
